// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { DataCache } from '@shared/commons/DataCache';
import { Algorithm, AlgorithmDataRange, AlgorithmType, AlgorithmSubscribeType } from '@shared/commons/cache/Algorithm/Algorithm';
import { GainerLoserData } from '@shared/commons/cache/Algorithm/GainersLosers/GainerLoserData';
import { Behavior } from '@shared/utils/GainersLosers/GainersLosers.types';
import { Resources } from '@shared/localizations/Resources';
import GainersLosersPanelTemplate from '@front/templates/GainersLosersPanelTemplate.html';
import { PanelNames } from '../UtilsClasses/FactoryConstants';
import { TerceraLinkControlConstants } from '../UtilsClasses/TerceraLinkControlConstants';
import { GainersLosersItem } from '../cache/GainersLosersItem';
import { Control } from '../elements/Control';
import { ColouringModes } from '../elements/QuickTable/QuickTableColumn';
import { QuickTableRactive } from '../elements/QuickTable/QuickTableRactive';
import { LinkedSystem } from '../misc/LinkedSystem';
import { DynProperty } from '@shared/commons/DynProperty';
import { ApplicationPanelWithTable } from './ApplicationPanelWithTable';
import { createTagDictionary, NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
export class GainersLosersPanel extends ApplicationPanelWithTable {
    constructor() {
        super();
        this._savedExchangeStateDict = {};
        this._ignoreTableSelectionChange = false;
        this.jbInit = () => {
            const gainersItems = new GainersLosersItem(new GainerLoserData(Behavior.Gainer));
            const losersItems = new GainersLosersItem(new GainerLoserData(Behavior.Loser));
            this.gainersQTR = this.createTable(gainersItems);
            this.losersQTR = this.createTable(losersItems);
            const qtGainers = this.gainersQTR.quickTable;
            const qtLosers = this.losersQTR.quickTable;
            if (qtGainers == null || qtLosers == null) {
                return;
            }
            this.deserializeDelayedData();
            qtGainers.UpdateSortedColumns();
            qtLosers.UpdateSortedColumns();
            this.OnResize.Subscribe(this.layoutTable, this);
            super.on('selectedAlgorithmChanged', this.algorithmsCombobox_SelectedIndexChanged);
            super.on('exchangeFilterChanged', this.exchangeComboBox_ValuesChanged);
            super.on('selectedTimeRangeChanged', this.rangeComboBox_SelectedIndexChanged);
            qtGainers.beforeTableContextMenuShowing = super.preparePopup.bind(this);
            qtLosers.beforeTableContextMenuShowing = super.preparePopup.bind(this);
            qtGainers.isMoveColumnAllowed = qtLosers.isMoveColumnAllowed = false;
            this.SetColumnsColouringMode(qtGainers);
            this.SetColumnsColouringMode(qtLosers);
            this.localize();
            this.layoutTable();
        };
        this.layoutTable = () => {
            [this.gainersQTR, this.losersQTR].forEach((qtr) => {
                if (!isNullOrUndefined(qtr)) {
                    super.layoutTableResize(qtr);
                }
            });
            const panel = this.getClientPanel();
            if (!isNullOrUndefined(panel)) {
                this.set({
                    showNoDataIcon: panel.offsetWidth > this.MIN_PANEL_WIDTH_FOR_ICON
                });
            }
        };
        this.symbolLink_Out = (newSubscriber, instrument) => {
            if (instrument == null) {
                return;
            }
            const color = super.get('symbolLinkValue');
            if (color !== TerceraLinkControlConstants.STATE_NONE) {
                LinkedSystem.setSymbol(color, instrument, newSubscriber);
            }
        };
        this.MIN_PANEL_WIDTH_FOR_ICON = 154; /* #130163 */
        this.createTable = (items) => {
            const qtr = new QuickTableRactive();
            super.addControl(qtr, true);
            const qt = qtr.quickTable;
            qt.allowGroupBy = true;
            qt.InitializeDirect(items);
            qt.UpdateSortedColumns();
            return qtr;
        };
        this.clearTable = (tableBehavior) => {
            const quickTableR = tableBehavior === Behavior.Gainer ? this.gainersQTR : this.losersQTR;
            if (quickTableR == null) {
                return;
            }
            const qt = quickTableR === null || quickTableR === void 0 ? void 0 : quickTableR.quickTable;
            if (qt == null) {
                return;
            }
            qt.ClearAll();
        };
        this.PopulateTables = () => {
            const gainersTable = this.gainersTable;
            const losersTable = this.losersTable;
            if (gainersTable == null || losersTable == null) {
                return;
            }
            const lastGainersSelectedInstrument = this.GetTableSelectedInstrument(gainersTable);
            const lastLosersSelectedInstrument = this.GetTableSelectedInstrument(losersTable);
            const gainersScrollPos = gainersTable.getFirstVisibleScrollIndex();
            const losersScrollPos = losersTable.getFirstVisibleScrollIndex();
            const gainersExpandCollapseState = this.GetTableExpandCollapseState(gainersTable);
            const losersExpandCollapseState = this.GetTableExpandCollapseState(losersTable);
            this._ignoreTableSelectionChange = true;
            gainersTable.ClearAll();
            losersTable.ClearAll();
            this._ignoreTableSelectionChange = false;
            const gainers = this.GetSortedTopItems(Behavior.Gainer, this.lastAlgorithmTop, this.lastGainersList, this.GetSelectedExchanges());
            const losers = this.GetSortedTopItems(Behavior.Loser, this.lastAlgorithmTop, this.lastLosersList, this.GetSelectedExchanges());
            void this.set('noDataForGainers', gainers.length === 0);
            void this.set('noDataForLosers', losers.length === 0);
            gainers.forEach((gainer) => gainersTable.AddItem(new GainersLosersItem(gainer)));
            losers.forEach((loser) => losersTable.AddItem(new GainersLosersItem(loser)));
            this.RestoreTableState(gainersTable, gainersExpandCollapseState, lastGainersSelectedInstrument, gainersScrollPos);
            this.RestoreTableState(losersTable, losersExpandCollapseState, lastLosersSelectedInstrument, losersScrollPos);
        };
        this.RestoreTableState = (table, expandCollapseState, lastSelectedItem, lastScrollPosition) => {
            table.sortRowsArray();
            this.RestoreTableSelection(table, lastSelectedItem);
            table.scroll.moveScrollToElement(lastScrollPosition);
        };
        this.RestoreTableSelection = (table, instrumentName) => {
            for (const row of table.rowsArray) {
                if (instrumentName === row.item.Data.InstrumentName) {
                    this._ignoreTableSelectionChange = true;
                    table.selectById([row.id]);
                    table.OnScroll(); // for redraw
                    this._ignoreTableSelectionChange = false;
                    return;
                }
            }
        };
        this.selectionChange = (onGainersTable, fromMouseClick) => __awaiter(this, void 0, void 0, function* () {
            if (fromMouseClick == null) {
                return;
            }
            const selectTable = onGainersTable ? this.gainersTable : this.losersTable;
            const anotherTable = onGainersTable ? this.losersTable : this.gainersTable;
            if (selectTable == null || anotherTable == null) {
                return;
            }
            let selectedRowNum = selectTable.selectedRowIds.length;
            if (selectedRowNum > 1) {
                selectTable.selectedRowIds = [selectTable.selectedRowIds[selectedRowNum - 1]];
                selectedRowNum = 1;
                selectTable.OnScroll(); // for redraw
            }
            if (selectedRowNum === 1 && !this._ignoreTableSelectionChange) {
                const selectedRow = selectTable.getItemById(selectTable.selectedRowIds[0]);
                const data = selectedRow.item.Data;
                const selectedIns = yield DataCache.getInstrumentByInstrumentTradableID_NFL(data.TradableId, data.RouteId, null, true);
                if (!isNullOrUndefined(selectedIns)) {
                    this.symbolLink_Out(false, selectedIns === null || selectedIns === void 0 ? void 0 : selectedIns.GetInteriorID());
                }
            }
            if (anotherTable != null && anotherTable.selectedRowIds.length > 0) {
                anotherTable.selectedRowIds = [];
                anotherTable.OnScroll(); // for redraw
            }
        });
        this.GetTableSelectedInstrument = (table) => {
            var _a;
            if (table.selectedRowIds.length > 0) {
                const row = table.rows[table.selectedRowIds[0]];
                const item = row != null ? row.item : null;
                return (_a = item === null || item === void 0 ? void 0 : item.Data) === null || _a === void 0 ? void 0 : _a.InstrumentName;
            }
            return '';
        };
        this.GetTableExpandCollapseState = (table) => {
            const pairs = new Map();
            if (table == null) {
                return pairs;
            }
            for (let i = 0; i < table.rowsArray.length; i++) {
                const currentRow = table.rowsArray[i];
                if (currentRow.isGroupRow) {
                    pairs.set(currentRow.GroupValue, currentRow.collapsed);
                }
            }
            return pairs;
        };
        this.GetSortedTopItems = (behavior, top, items, exchanges) => {
            var _a;
            if (this.rangeComboBox == null || !top) {
                return items;
            }
            // 1. Get filtered by exchange
            const selectedRange = (_a = this.rangeComboBox.get('selectedItem')) === null || _a === void 0 ? void 0 : _a.value;
            let last = items.filter((item) => exchanges.includes(item.MarketDataExchange) &&
                (item.Range == null ||
                    (selectedRange != null && item.Range.isEqual(AlgorithmDataRange.fromString(selectedRange)))));
            // 2. Order by Interest change and Market data
            last =
                behavior === Behavior.Gainer
                    ? last.sort((a, b) => b.InterestChange - a.InterestChange ||
                        a.MarketDataExchange.localeCompare(b.MarketDataExchange))
                    : last.sort((a, b) => a.InterestChange - b.InterestChange ||
                        a.MarketDataExchange.localeCompare(b.MarketDataExchange));
            // 3. Take TOP
            return last.slice(0, top);
        };
        this.isAllowByProductSubscription = (gainerLoserData) => {
            var _a;
            const entitlmentSystemCache = DataCache.EntitlementManager;
            const instrumentId = (_a = gainerLoserData.InstrumentId) === null || _a === void 0 ? void 0 : _a.toString();
            const instrumentGroupId = gainerLoserData.InstrumentGroupId;
            const tradingExchange = gainerLoserData.TradingExchange;
            if (gainerLoserData.IsDelayed) {
                // Don't have RealTime subscription and have Delayed
                return (!entitlmentSystemCache.IsExistSubscription(false, instrumentId, instrumentGroupId, tradingExchange) &&
                    entitlmentSystemCache.IsExistSubscription(true, instrumentId, instrumentGroupId, tradingExchange));
            }
            else {
                return entitlmentSystemCache.IsExistSubscription(false, instrumentId, instrumentGroupId, tradingExchange);
            }
        };
        this.GetSelectedExchanges = () => {
            var _a;
            const items = (_a = this.exchangeComboBox) === null || _a === void 0 ? void 0 : _a.get('items');
            if (items == null || items.length === 0) {
                return [];
            }
            else {
                return items.filter((item) => item === null || item === void 0 ? void 0 : item.checked).map((item) => item === null || item === void 0 ? void 0 : item.value);
            }
        };
        this.updateTableByMessage = (gainersLosersMessage /* DirectAlgorithmGainersLosersMessage */) => {
            const gainers = [];
            const losers = [];
            const exchanges = [];
            for (const gainersLosersData of gainersLosersMessage.GainersLosersData) {
                if (!this.isAllowByProductSubscription(gainersLosersData)) {
                    continue;
                }
                if (!exchanges.includes(gainersLosersData.MarketDataExchange)) {
                    exchanges.push(gainersLosersData.MarketDataExchange);
                }
                if (gainersLosersData.Behavior === Behavior.Gainer) {
                    gainers.push(gainersLosersData);
                }
                else {
                    losers.push(gainersLosersData);
                }
            }
            this.lastGainersList = gainers;
            this.lastLosersList = losers;
            this.lastAlgorithmTop = gainersLosersMessage.AlgorithmTop;
            this.populateExchanges(exchanges);
            this.PopulateTables();
        };
        this.populateRanges = (availableRanges /* AlgorithmDataRange[] */) => {
            this.isRangesPopulating = true;
            this.rangeComboBox.clearItems();
            let selectedValue = '';
            availableRanges.forEach((range) => {
                const rangeStr = range.toString();
                const comboItem = {
                    type: NewContextMenuItemType.Default,
                    text: Algorithm.getRangeString(range),
                    value: rangeStr
                };
                if (this._savedRange != null && this._savedRange.toString() === rangeStr) {
                    selectedValue = rangeStr;
                }
                this.rangeComboBox.addItem(comboItem);
            });
            this.rangeComboBox.set('visible', true);
            this.rangeComboBox.set('enabled', availableRanges.length > 1);
            if (selectedValue.length > 0) {
                this.rangeComboBox.setItembyValue(selectedValue);
            }
            this.isRangesPopulating = false;
        };
        this.populateAlgorithmsComboBox = () => {
            var _a;
            (_a = this.algorithmsComboBox) === null || _a === void 0 ? void 0 : _a.clearItems();
            const algorithmCache = DataCache.AlgorithmCache;
            if (algorithmCache != null) {
                const algorithms = [
                    ...algorithmCache.getAlgorithmsByAlgorithmType(AlgorithmType.GainersLosers),
                    ...algorithmCache.getAlgorithmsByAlgorithmType(AlgorithmType.HistoricalGainersLosersTop)
                ];
                algorithms.sort((x, y) => x.AlgorithmName.localeCompare(y.AlgorithmName));
                for (const algorithm of algorithms) {
                    const comboItem = {
                        type: NewContextMenuItemType.Default,
                        text: algorithm.AlgorithmName,
                        value: algorithm.AlgorithmId
                    };
                    this.algorithmsComboBox.addItem(comboItem);
                }
            }
            const algoCBItems = this.algorithmsComboBox.get('items');
            this.algorithmsComboBox.set('visible', algoCBItems.length > 1);
            if (algoCBItems.length > 0) {
                if (this._subscribedAlgorithmIdProp == null) {
                    this._subscribedAlgorithmIdProp = algoCBItems[0].value;
                }
                this.algorithmsComboBox.setItembyValue(this._subscribedAlgorithmIdProp);
            }
        };
        this.algorithmsCombobox_SelectedIndexChanged = (context, selectedValue) => {
            const algorithmCache = DataCache.AlgorithmCache;
            if (this._subscribedAlgorithmId != null && this._subscribedAlgorithmId !== -1) {
                DataCache.AlgorithmCache.SubscriptionHandler(AlgorithmSubscribeType.Unsubscribe, this._subscribedAlgorithmId, this);
            }
            this.clearTable(Behavior.Gainer);
            this.clearTable(Behavior.Loser);
            if (selectedValue != null && selectedValue !== -1) {
                this._subscribedAlgorithmId = selectedValue;
                const algorithm = algorithmCache.getAlgorithmByAlgorithmId(this._subscribedAlgorithmId);
                if (algorithm != null && algorithm.AlgorithmType === AlgorithmType.HistoricalGainersLosersTop) {
                    this.populateRanges(algorithm.AvailableRanges);
                }
                else {
                    this.rangeComboBox.set('visible', false);
                }
                DataCache.AlgorithmCache.SubscriptionHandler(AlgorithmSubscribeType.Subscribe, this._subscribedAlgorithmId, this);
                this._algorithmChanged = true;
            }
        };
        this.rangeComboBox_SelectedIndexChanged = (context, selectedValue) => {
            if (this.isRangesPopulating) {
                return;
            }
            this._savedRange = selectedValue;
            this.PopulateTables();
        };
        this.exchangeComboBox_ValuesChanged = (context, newValue) => {
            this._savedExchangeStateDict[this._subscribedAlgorithmId] = super
                .get('exchangeFilterItems')
                .filter((e) => e.checked)
                .map((e) => e.value);
            this.PopulateTables();
        };
        this.populateExchanges = (newExchanges) => {
            if (this._savedExchangeStateDict[this._subscribedAlgorithmId] == null) {
                this._savedExchangeStateDict[this._subscribedAlgorithmId] = newExchanges;
            }
            let isRepopulateExchange = false;
            const exchangeCB = this.exchangeComboBox;
            if (exchangeCB == null) {
                return;
            }
            const CBItemsBeforePopulate = exchangeCB.get('items');
            const oldExchanges = [];
            if (this._algorithmChanged) {
                this._algorithmChanged = false;
                // задаем предыдущие значения
                exchangeCB.set('items', this._savedExchangeStateDict[this._subscribedAlgorithmId].map((e) => ({
                    text: e,
                    checked: true,
                    value: e
                })));
                isRepopulateExchange = true;
            }
            const oldCBItems = exchangeCB.get('items');
            if (oldCBItems != null) {
                oldCBItems.forEach((element) => oldExchanges.push(element.value));
            }
            newExchanges.sort((a, b) => a.localeCompare(b));
            oldExchanges.sort((a, b) => a.localeCompare(b));
            const boolValues = (oldCBItems === null || oldCBItems === void 0 ? void 0 : oldCBItems.filter((e) => e.checked)) || [];
            if (!isRepopulateExchange && newExchanges.toString() === oldExchanges.toString()) {
                return;
            }
            const valuesForPopulate = boolValues != null ? [...boolValues] : [];
            for (let i = 0; i < newExchanges.length; i++) {
                // New Exchange
                if (oldExchanges.filter((e) => e === newExchanges[i]).length === 0) {
                    valuesForPopulate.push({
                        value: newExchanges[i],
                        checked: this._savedExchangeStateDict[this._subscribedAlgorithmId].includes(newExchanges[i]) ||
                            CBItemsBeforePopulate.length === boolValues.length, // If all exchanges are selected, then the new exchange must also be selected #119172
                        text: newExchanges[i]
                    });
                }
            }
            if (valuesForPopulate.length > 0) {
                exchangeCB.set({ items: valuesForPopulate, visible: newExchanges.length !== 1 });
            }
        };
        this.SetColumnsColouringMode = (table) => {
            super.ProcessSetColumnsColouringMode(table, [1], ColouringModes.Signed);
            super.ProcessSetColumnsColouringMode(table, [4], ColouringModes.Previous);
        };
        this.createContextMenu = () => {
            var _a, _b;
            const menuItems = this.createMenuItems();
            this.menuTagDict = createTagDictionary(menuItems);
            (_a = this.gainersTable) === null || _a === void 0 ? void 0 : _a.setTableContextMenuItems(menuItems);
            (_b = this.losersTable) === null || _b === void 0 ? void 0 : _b.setTableContextMenuItems(menuItems);
        };
        this.createMenuItems = () => {
            const items = [];
            items.push({
                type: NewContextMenuItemType.Default,
                text: 'panel.positions.menu.View',
                svgImage: 'View',
                visible: !Resources.isHidden('panel.GainersLosersPanel.menu.ShowToolbar'),
                subitems: [
                    {
                        type: NewContextMenuItemType.Default,
                        text: 'panel.positions.menu.ShowToolbar',
                        checked: super.get('showToolbar'),
                        canCheck: true,
                        event: this.ShowToolbarStateChange.bind(this)
                    }
                ]
            });
            return items;
        };
        this.ShowToolbarStateChange = (menuItem) => {
            this.ShowToolbarStateUpdate(menuItem.checked);
        };
        this.ShowToolbarStateUpdate = (state) => {
            void this.set('showToolbar', state);
            this.layoutTable();
        };
        this.Properties = () => {
            var _a;
            const props = super.Properties();
            let dp = new DynProperty('SelectedExchangesState', JSON.stringify(this._savedExchangeStateDict), DynProperty.STRING, DynProperty.HIDDEN_GROUP);
            props.push(dp);
            dp = new DynProperty('SelectedAlgorithm', this._subscribedAlgorithmId, DynProperty.INTEGER, DynProperty.HIDDEN_GROUP);
            props.push(dp);
            dp = new DynProperty('SelectedRange', (_a = this._savedRange) === null || _a === void 0 ? void 0 : _a.toString(), DynProperty.STRING, DynProperty.HIDDEN_GROUP);
            props.push(dp);
            return props;
        };
        this.callBack = (properties) => {
            super.callBack(properties);
            let prop = DynProperty.getPropertyByName(properties, 'SelectedExchangesState');
            if (prop != null) {
                this._savedExchangeStateDict = JSON.parse(prop.value);
            }
            prop = DynProperty.getPropertyByName(properties, 'SelectedAlgorithm');
            if (prop != null) {
                this._subscribedAlgorithmIdProp = prop.value;
            }
            prop = DynProperty.getPropertyByName(properties, 'SelectedRange');
            if ((prop === null || prop === void 0 ? void 0 : prop.value) != null) {
                this._savedRange = AlgorithmDataRange.fromString(prop.value);
            }
        };
        this.Name = 'GainersLosersPanel';
        this.headerLocaleKey = 'panel.GainersLosersPanel';
        this.NeedCalculateRowCount = false;
    }
    getType() {
        return PanelNames.GainersLosersPanel;
    }
    get gainersTable() {
        var _a;
        return (_a = this.gainersQTR) === null || _a === void 0 ? void 0 : _a.quickTable;
    }
    get losersTable() {
        var _a;
        return (_a = this.losersQTR) === null || _a === void 0 ? void 0 : _a.quickTable;
    }
    oninit() {
        super.oninit();
        Control.Ticker.Subscribe(this.TickAsync, this);
    }
    oncomplete() {
        var _a, _b;
        super.oncomplete();
        this.createContextMenu();
        this.repopulate();
        this.localize();
        (_a = this.gainersTable) === null || _a === void 0 ? void 0 : _a.OnSelectionChanged.Subscribe(this.selectionChange.bind(this, true), this);
        (_b = this.losersTable) === null || _b === void 0 ? void 0 : _b.OnSelectionChanged.Subscribe(this.selectionChange.bind(this, false), this);
    }
    dispose() {
        var _a, _b;
        this.algorithmsComboBox.clearItems(); // <- unsubscribe algorithm
        Control.Ticker.UnSubscribe(this.TickAsync, this);
        (_a = this.gainersTable) === null || _a === void 0 ? void 0 : _a.OnSelectionChanged.UnSubscribe(this.selectionChange, this);
        (_b = this.losersTable) === null || _b === void 0 ? void 0 : _b.OnSelectionChanged.UnSubscribe(this.selectionChange, this);
        super.dispose();
    }
    localize() {
        super.localize();
        const exchangesCB = this.exchangeComboBox;
        if (exchangesCB != null) {
            const noExchText = Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.No exchanges');
            exchangesCB.set({
                allItemsSelectedText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.All exchanges'),
                noItemsSelectedText: noExchText,
                noItemsText: noExchText,
                fewItemsSelectedPostfixText: Resources.getResource('panel.TerceraSymbolLookupDropDownForm.exchangeComboBox.exchanges')
            });
        }
        if (this.gainersTable != null) {
            if (this.gainersTable.needRedraw) {
                this.gainersTable.localize();
            }
        }
        if (this.losersTable != null) {
            if (this.losersTable.needRedraw) {
                this.losersTable.localize();
            }
        }
        void this.set('noDataText', Resources.getResource('panel.GainersLosersPanel.noData'));
    }
    repopulate() {
        super.repopulate();
        this.populateAlgorithmsComboBox();
    }
    TickAsync() {
        this.quickTableTickAsync(this.gainersTable);
        this.quickTableTickAsync(this.losersTable);
        this.forceUpdate = false;
    }
    ProcessAlgorithm(msg /* DirectAlgorithmGainersLosersMessage */) {
        this.updateTableByMessage(msg);
    }
    themeChange() {
        if (!isNullOrUndefined(this.gainersTable)) {
            this.gainersTable.themeChange();
        }
        if (!isNullOrUndefined(this.losersTable)) {
            this.losersTable.themeChange();
        }
    }
    get algorithmsComboBox() {
        var _a;
        return (_a = this.Controls) === null || _a === void 0 ? void 0 : _a.AlgorithmsList;
    }
    get exchangeComboBox() {
        var _a;
        return (_a = this.Controls) === null || _a === void 0 ? void 0 : _a.ExchangeFilter;
    }
    get rangeComboBox() {
        var _a;
        return (_a = this.Controls) === null || _a === void 0 ? void 0 : _a.TimeRange;
    }
    // #region ISerializable
    serialize() {
        const serializedData = super.serialize();
        serializedData.table = {
            gainers: this.gainersTable != null ? this.gainersTable.serialize() : {},
            losers: this.losersTable != null ? this.losersTable.serialize() : {}
        };
        return serializedData;
    }
    deserialize(serialized) {
        super.deserialize(serialized);
        if (!this.completed) {
            return;
        }
        this.gainersTable.deserialize(serialized.table.gainers);
        this.losersTable.deserialize(serialized.table.losers);
    }
    deserializeOldScheme(serialized) {
        var _a, _b;
        super.deserializeOldScheme(serialized);
        if (!this.completed) {
            return;
        }
        const gainersSettings = (_a = serialized.xmlSettingsTemplate) === null || _a === void 0 ? void 0 : _a.GainersSettings;
        const losersSettings = (_b = serialized.xmlSettingsTemplate) === null || _b === void 0 ? void 0 : _b.LosersSettings;
        if (gainersSettings != null) {
            this.gainersTable.deserializeOldScheme(gainersSettings);
        }
        if (losersSettings != null) {
            this.losersTable.deserializeOldScheme(losersSettings);
        }
    }
}
ApplicationPanelWithTable.extendWith(GainersLosersPanel, {
    partials: { bodyPartial: GainersLosersPanelTemplate },
    data: function () {
        return {
            canLinkByAccount: false,
            isSymbolLinkShow: true,
            exchangeFilterItems: [],
            showToolbar: true,
            showNoDataIcon: true /* Controls visibility of the NoData icon; it is hidden when the panel width is less than MIN_PANEL_WIDTH_FOR_ICON, even if the table has no data. */,
            noDataText: '',
            noDataForGainers: true,
            noDataForLosers: true,
            timeRangeFilterVisible: true
        };
    }
});
